import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "@/lib/toast";

/**
 * Single source of truth for the PWA install lifecycle.
 *
 * • Captures the `beforeinstallprompt` event once at app root so both the
 *   dashboard nudge AND the sidebar "Get the App" link can trigger the
 *   native install prompt from the same deferred event.
 * • Fires a warm sonner toast when the browser reports `appinstalled` (the
 *   user has just added MFG to their home screen or app launcher).
 * • Detects iOS Safari (no `beforeinstallprompt` support) and exposes an
 *   `isIOS` flag so downstream UI can swap to manual "Share → Add to Home
 *   Screen" copy.
 */
const PwaInstallContext = createContext({
  isInstalled: false,
  canInstall: false,
  isIOS: false,
  promptInstall: async () => "unavailable",
});

const APP_INSTALL_TOASTED = "mfg_pwa_install_toasted_v1";

function detectInstalled() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    window.navigator.standalone === true
  );
}

function detectIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function PwaInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => detectInstalled());
  const [isIOS] = useState(() => detectIOS());

  useEffect(() => {
    const onBeforePrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
      // Only toast the first time so users don't get spammed on reinstalls
      try {
        if (!localStorage.getItem(APP_INSTALL_TOASTED)) {
          toast.success("MFG is on your home screen 🎉", {
            description: "Open it anytime — signed in, offline BOL upload, push alerts, all built in.",
            duration: 8000,
          });
          localStorage.setItem(APP_INSTALL_TOASTED, "1");
        }
      } catch { /* ignore private mode */ }
    };
    const media = window.matchMedia("(display-mode: standalone)");
    const onModeChange = () => setIsInstalled(detectInstalled());

    window.addEventListener("beforeinstallprompt", onBeforePrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (media.addEventListener) media.addEventListener("change", onModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforePrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (media.removeEventListener) media.removeEventListener("change", onModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return "unavailable";
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setDeferredPrompt(null);
      return outcome;
    } catch {
      return "unavailable";
    }
  }, [deferredPrompt]);

  const value = useMemo(() => ({
    isInstalled,
    canInstall: !!deferredPrompt,
    isIOS,
    promptInstall,
  }), [isInstalled, deferredPrompt, isIOS, promptInstall]);

  return (
    <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>
  );
}

export function usePwaInstall() {
  return useContext(PwaInstallContext);
}
